import { router } from "@inertiajs/react"
import { Loader2 } from "lucide-react"
import { useEffect, useState } from "react"
import { toast } from "sonner"
import { ActionHintTooltip } from "@/components/ui/action-hint-tooltip"
import { useProgressClock } from "@/hooks/use-progress-clock"
import { useTranslation } from "@/hooks/use-translation"
import { estimatedJobProgress, isEstimatedGenerationPhase } from "@/lib/job-estimated-progress"
import { cn } from "@/lib/utils"
import { type ShotMediaVersionFrameRow, activateShotMediaVersion, fetchShotMediaVersions } from "@/lib/workspace-api"
import type { GenerationJobRow } from "@/types/workspace"

/** Сколько последних версий видно на полке; остальные — по «+N» в колонке версий. */
const VISIBLE_VERSIONS = 5

type Props = {
  projectId: number
  shotId: number
  kind: "storyboard" | "shooting_board"
  /** Меняется, когда у шота сменилась активная версия или пришла новая — перечитываем список. */
  refreshKey: string
  /** Идущие генерации этого вида — по слоту на каждую будущую версию (параллельные запуски тоже). */
  generatingJobs?: GenerationJobRow[]
  onActivated: () => void
  onOpenAll: () => void
}

/** Миниатюры версий ассета на полке под ним: клик делает версию основной. */
export function ShotVersionStrip({
  projectId,
  shotId,
  kind,
  refreshKey,
  generatingJobs = [],
  onActivated,
  onOpenAll,
}: Props) {
  const { t, i18n } = useTranslation(["shot", "common"])
  const [versions, setVersions] = useState<ShotMediaVersionFrameRow[]>([])
  const [activatingId, setActivatingId] = useState<number | null>(null)
  const now = useProgressClock(generatingJobs.some(isEstimatedGenerationPhase))

  // Быстрое переключение шотов: ответ по прошлому шоту отменяется и не перетирает полку нового.
  useEffect(() => {
    const controller = new AbortController()
    fetchShotMediaVersions(projectId, shotId, kind, { signal: controller.signal })
      .then(setVersions)
      .catch(() => {
        // Полка без версий не мешает работе: колонка версий покажет ошибку сама.
      })
    return () => controller.abort()
  }, [projectId, shotId, kind, refreshKey])

  const activate = async (versionId: number) => {
    if (activatingId != null) return
    setActivatingId(versionId)
    try {
      const result = await activateShotMediaVersion(projectId, shotId, versionId)
      if (!result.ok) {
        toast.error(result.error ?? t("mediaVersions.activateFailed"))
        return
      }
      setVersions((list) => list.map((v) => ({ ...v, active: v.id === versionId })))
      onActivated()
      router.reload({ only: ["tree"] })
    } catch {
      toast.error(t("common.networkUnavailable"))
    } finally {
      setActivatingId(null)
    }
  }

  // Номер версии — по порядку создания, как в колонке версий.
  const ordered = [...versions].sort((a, b) => a.created_at.localeCompare(b.created_at) || a.id - b.id)
  const numbered = ordered.map((v, i) => ({ version: v, n: i + 1 }))
  const visible = numbered.slice(-VISIBLE_VERSIONS)
  const hidden = numbered.length - visible.length
  const dateFormat = new Intl.DateTimeFormat(i18n.language, {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  })

  if (numbered.length <= 1 && generatingJobs.length === 0) return null

  return (
    <div className="flex shrink-0 items-center gap-1.5">
      {hidden > 0 ? (
        <button
          type="button"
          className="flex h-8 min-w-8 items-center justify-center rounded-sm px-1.5 text-[11px] text-ui-text-muted tabular-nums transition-colors hover:bg-ui-surface-hover hover:text-ui-text-primary"
          onClick={onOpenAll}
          aria-label={t("assetShelf.allVersions", { count: numbered.length })}
        >
          +{hidden}
        </button>
      ) : null}
      {visible.map(({ version, n }) => {
        const thumb = version.preview_thumb_url ?? version.preview_url ?? null
        const tip = version.active
          ? t("assetShelf.activeVersion", { n })
          : t("assetShelf.makeActive", { n, date: dateFormat.format(new Date(version.created_at)) })
        return (
          <ActionHintTooltip key={version.id} label={tip}>
            <button
              type="button"
              aria-label={tip}
              aria-pressed={version.active}
              disabled={activatingId != null}
              onClick={() => (version.active ? undefined : void activate(version.id))}
              className={cn(
                "relative h-8 w-14 shrink-0 overflow-hidden rounded-sm bg-black transition-opacity",
                version.active
                  ? "opacity-100 ring-[1.5px] ring-ui-text-primary"
                  : "opacity-55 hover:opacity-90 disabled:hover:opacity-55",
              )}
            >
              {thumb ? <img src={thumb} alt="" className="h-full w-full object-cover" loading="lazy" /> : null}
              <span className="absolute bottom-0.5 left-0.5 rounded-xs bg-black/65 px-1 text-[9px] leading-[11px] text-white">
                {t("assetShelf.versionLabel", { n })}
              </span>
              {activatingId === version.id ? (
                <span className="absolute inset-0 flex items-center justify-center bg-black/50">
                  <Loader2 className="h-3.5 w-3.5 animate-spin text-white" />
                </span>
              ) : null}
            </button>
          </ActionHintTooltip>
        )
      })}
      {generatingJobs.map((job, i) => {
        const n = numbered.length + 1 + i
        const progress = estimatedJobProgress(job, now)
        const tip = t("assetShelf.generatingVersionN", { n, progress })
        return (
          <ActionHintTooltip key={job.id} label={tip}>
            <span
              className="relative flex h-8 w-14 shrink-0 items-center justify-center overflow-hidden rounded-sm bg-ui-accent/20 ring-1 ring-ui-accent/40"
              role="img"
              aria-label={tip}
            >
              <Loader2 className="h-3.5 w-3.5 animate-spin text-ui-accent-text" />
              <span className="absolute bottom-0.5 left-0.5 px-1 text-[9px] leading-[11px] text-ui-accent-text">
                {t("assetShelf.versionLabel", { n })}
              </span>
              <span
                className="absolute inset-x-0 bottom-0 h-0.5 bg-ui-accent transition-[width] duration-700 ease-out"
                style={{ width: `${Math.min(100, progress)}%` }}
                aria-hidden
              />
            </span>
          </ActionHintTooltip>
        )
      })}
    </div>
  )
}
